import { Suspense, lazy, useState } from "react";
import type { DeliveryZoneDto, DeliveryZoneInput } from "@school-sales-ai/shared";
import { useGeocodeSearch } from "@/hooks/useDeliveryZones";
import type { BackgroundZone } from "./ZoneMap";
import {
  emptyZoneForm,
  toZoneInput,
  zoneToForm,
  type ZoneFormField,
  type ZoneFormValues,
} from "./zone-form";

const ZoneMap = lazy(() => import("./ZoneMap").then((m) => ({ default: m.ZoneMap })));

type Props = {
  zone: DeliveryZoneDto | null;
  /** The shop's other zones, drawn read-only so a new border can follow theirs. */
  otherZones: BackgroundZone[];
  color: string;
  geocodingConfigured: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: DeliveryZoneInput) => void;
};

/**
 * Create or edit one delivery zone: a name, the fee charged inside it, and the
 * area itself, drawn on the map. Validation runs on submit through the shared
 * schema, so this refuses exactly what the server would.
 */
export function DeliveryZoneEditorDialog({
  zone,
  otherZones,
  color,
  geocodingConfigured,
  saving,
  onCancel,
  onSave,
}: Props): React.JSX.Element {
  const [values, setValues] = useState<ZoneFormValues>(() =>
    zone ? zoneToForm(zone) : emptyZoneForm(),
  );
  const [errors, setErrors] = useState<Map<ZoneFormField, string>>(new Map());
  const [search, setSearch] = useState("");
  const [focus, setFocus] = useState<{ lat: number; lng: number; key: number } | null>(null);
  const [searchMiss, setSearchMiss] = useState(false);
  const geocode = useGeocodeSearch();

  const set = <K extends keyof ZoneFormValues>(key: K, value: ZoneFormValues[K]): void =>
    setValues((v) => ({ ...v, [key]: value }));

  const runSearch = (): void => {
    const query = search.trim();
    if (!query) return;
    setSearchMiss(false);
    geocode.mutate(query, {
      onSuccess: (result) => {
        if (!result) {
          setSearchMiss(true);
          return;
        }
        setFocus({ lat: result.lat, lng: result.lng, key: Date.now() });
      },
    });
  };

  const submit = (): void => {
    const result = toZoneInput(values);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors(new Map());
    onSave(result.input);
  };

  const err = (key: ZoneFormField): React.JSX.Element | null => {
    const message = errors.get(key);
    return message ? <p className="mt-1 text-xs text-red-600">{message}</p> : null;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={zone ? "Editar zona de entrega" : "Nueva zona de entrega"}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
    >
      <div className="my-8 w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-base font-semibold text-ink-900">
          {zone ? "Editar zona de entrega" : "Nueva zona de entrega"}
        </h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-[13px] font-medium text-ink-900">Nombre</span>
            <input
              type="text"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={80}
              placeholder="Zona Centro"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-moss focus:outline-none"
            />
            {err("name")}
          </label>
          <label className="block">
            <span className="text-[13px] font-medium text-ink-900">Costo de envío</span>
            <span className="mt-1 flex items-center gap-2">
              <span className="text-[13px] text-ink-500">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={values.fee}
                onChange={(e) => set("fee", e.target.value)}
                maxLength={10}
                placeholder="0.00"
                aria-label="Costo de envío de la zona en pesos"
                className="w-32 rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-moss focus:outline-none"
              />
              <span className="text-[13px] text-ink-500">MXN</span>
            </span>
            {err("fee")}
          </label>
        </div>

        <div className="mt-4">
          <p className="text-[13px] font-medium text-ink-900">Área de entrega</p>
          <p className="mt-0.5 text-[12px] text-ink-500">
            Dibuja el contorno con la herramienta de polígono o de rectángulo. Puedes dibujar varias
            piezas: todas forman la misma zona. Para corregirla, usa editar o borrar en la barra del
            mapa.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  runSearch();
                }
              }}
              disabled={!geocodingConfigured}
              placeholder={
                geocodingConfigured
                  ? "Busca una colonia o dirección para ubicar el mapa"
                  : "La búsqueda de direcciones no está configurada"
              }
              aria-label="Buscar en el mapa"
              className="flex-1 rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-moss focus:outline-none disabled:bg-ink-050"
            />
            <button
              type="button"
              onClick={runSearch}
              disabled={!geocodingConfigured || geocode.isPending || !search.trim()}
              className="rounded-lg bg-ink-100 px-3 py-1.5 text-[13px] font-semibold text-ink-700 disabled:opacity-40"
            >
              {geocode.isPending ? "Buscando…" : "Buscar"}
            </button>
          </div>
          {searchMiss && (
            <p className="mt-1 text-xs text-ink-500">No encontramos ese lugar. Prueba con otra forma.</p>
          )}
          <Suspense fallback={<div className="mt-2 h-[360px] rounded-lg bg-ink-050" />}>
            <ZoneMap
              className="mt-2 h-[360px] w-full rounded-lg border border-ink-200"
              backgroundZones={otherZones}
              editable={{
                geometry: values.geometry,
                color,
                onChange: (geometry) => set("geometry", geometry),
              }}
              focus={focus}
            />
          </Suspense>
          {err("geometry")}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-[10px] bg-ink-100 px-4 py-2 text-[13px] font-semibold text-ink-700 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-[10px] bg-lime px-4 py-2 text-[13px] font-semibold text-moss disabled:opacity-40"
          >
            {saving ? "Guardando…" : "Guardar zona"}
          </button>
        </div>
      </div>
    </div>
  );
}
